import React, { useState, useContext } from 'react';
import { FaBookOpen } from 'react-icons/fa';
import styles from './UserGuide.module.css';
import { UserContext } from '../../../../context/userContext';

interface UserGuideProps {
  expanded: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const UserGuide: React.FC<UserGuideProps> = ({ expanded, onMouseEnter, onMouseLeave }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(UserContext);

  const handleGuideClick = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    setIsLoading(true);
    try {
      // Get client IP address
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const { ip: ipAddress } = await ipResponse.json();
      
      // Call the Guide API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Guide`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.accessToken}`,
            'Content-Type': 'application/json',
            'X-Forwarded-For': ipAddress,
            'X-Real-IP': ipAddress
          },
          body: JSON.stringify({
            clientIP: ipAddress,
            timestamp: new Date().toISOString(),
            source: 'csharp-api'
          })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        // Only open the guide if the API call was successful
        window.open(process.env.REACT_APP_ATLAS_USER_GUIDE_URL, '_blank');
      } else {
        console.error('Failed to record IP:', data.message);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error accessing guide:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={styles.menu + (expanded ? ' ' + styles.menuExpanded : '')}
      style={{ pointerEvents: expanded ? 'auto' : 'none' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        className={styles.menuButton}
        onClick={handleGuideClick}
        aria-label="Atlas User Guide"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        tabIndex={expanded ? 0 : -1}
        disabled={isLoading}
      >
        <FaBookOpen />
      </button>
      {showTooltip && (
        <div className={styles.tooltip}>
          {isLoading ? 'Loading Guide...' : 'Atlas User Guide'}
        </div>
      )}
    </div>
  );
};

export default UserGuide; 